/**
 * The feedback inbox's model (src/apps/website/FeedbackListView.jsx): one
 * list holding both kinds of feedback, with one set of statuses.
 *
 *   Suite      the Feedback tab (suiteFeedback, read through the admin
 *              callables): a bug, a suggestion or a question about a page.
 *   Planner    notes staff leave on a website planner session (Prospects >
 *              a session > Give Feedback), stored under
 *              plannerSessions/{id}/feedback/{id} and read by tools/feedback.js.
 *
 * Statuses: open, doing, done, won't fix. Planner notes keep their own
 * stored words so tools/feedback.js keeps working: unresolved is open,
 * resolved is done, dismissed is won't fix; doing is stored as "doing".
 */
export const TYPES = ["bug", "suggestion", "question"];
export const TYPE_LABELS = { bug: "Bug", suggestion: "Suggestion", question: "Question", planner: "Planner note" };
export const STATUSES = ["open", "doing", "done", "wontfix"];
export const STATUS_LABELS = { open: "Open", doing: "Doing", done: "Done", wontfix: "Won't fix" };
export const SOURCE_LABELS = { suite: "Suite", planner: "Website planner" };
export const ACTIVE = new Set(["open", "doing"]);

const PLANNER_TO_STATUS = { unresolved: "open", open: "open", doing: "doing", resolved: "done", done: "done", dismissed: "wontfix", wontfix: "wontfix" };
const STATUS_TO_PLANNER = { open: "unresolved", doing: "doing", done: "resolved", wontfix: "dismissed" };

export const plannerStatus = (stored) => PLANNER_TO_STATUS[stored] || "open";
export const plannerStoredStatus = (status) => STATUS_TO_PLANNER[status] || "unresolved";

const ms = (v) => {
  if (typeof v === "number") return v;
  if (typeof v === "string") { const t = Date.parse(v); return Number.isNaN(t) ? null : t; }
  return null;
};

/** A Suite report (listSuiteFeedback item) as an inbox row. */
export function suiteRow(item) {
  const page = item.page || {};
  return {
    key: `suite:${item.id}`,
    id: item.id,
    source: "suite",
    type: TYPES.includes(item.type) ? item.type : "bug",
    status: STATUSES.includes(item.status) ? item.status : "open",
    message: item.message || "",
    note: item.note || "",
    createdAt: ms(item.createdAt),
    personName: item.person?.name || item.person?.email || "Someone",
    personKey: item.person?.uid || item.person?.email || "unknown",
    appKey: page.app || "home",
    pageLabel: [page.appName || (page.app ? page.app : "Home"), page.tab].filter(Boolean).join(" › "),
    raw: item,
  };
}

/** A planner note (plannerSessions/{id}/feedback/{id}, with session fields) as an inbox row. */
export function plannerRow(fb) {
  const parts = [];
  if (fb.conversationFeedback) parts.push(fb.conversationFeedback);
  if (fb.reportFeedback) parts.push(fb.reportFeedback);
  const notes = (fb.conversationAnnotations?.length || 0) + (fb.reportAnnotations?.length || 0);
  if (!parts.length && notes) parts.push(`${notes} ${notes === 1 ? "annotation" : "annotations"}`);
  return {
    key: `planner:${fb._sessionId}:${fb._docId}`,
    id: fb._docId,
    sessionId: fb._sessionId,
    source: "planner",
    type: "planner",
    status: plannerStatus(fb.status),
    message: parts.join(" · "),
    note: fb.resolutionNote || "",
    createdAt: ms(fb.createdAt),
    personName: fb.createdBy || "Someone",
    personKey: fb.createdBy || "unknown",
    appKey: "website-planner",
    pageLabel: `Planner session · ${fb.sessionWhat || "Planner session"}`,
    raw: fb,
  };
}

export const DEFAULT_FILTERS = { source: "all", type: "all", status: "active", person: "all", app: "all" };

export function filterRows(rows, f = DEFAULT_FILTERS) {
  return rows.filter((r) =>
    (f.source === "all" || r.source === f.source) &&
    (f.type === "all" || r.type === f.type) &&
    (f.status === "all" || (f.status === "active" ? ACTIVE.has(r.status) : r.status === f.status)) &&
    (f.person === "all" || r.personKey === f.person) &&
    (f.app === "all" || r.appKey === f.app));
}

/** Open and doing first; among those, bugs first; then newest first. */
export function sortRows(rows) {
  const rank = (r) => (!ACTIVE.has(r.status) ? 2 : r.type === "bug" ? 0 : 1);
  return [...rows].sort((a, b) => rank(a) - rank(b) || (b.createdAt ?? 0) - (a.createdAt ?? 0));
}

/** The choices the person and app/page filters offer, from the rows there are. */
export function filterChoices(rows) {
  const people = new Map();
  const apps = new Map();
  for (const r of rows) {
    if (!people.has(r.personKey)) people.set(r.personKey, r.personName);
    if (!apps.has(r.appKey)) apps.set(r.appKey, r.appKey === "website-planner" ? "Website planner" : (r.raw?.page?.appName || r.appKey));
  }
  const sorted = (m) => [...m.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  return { people: sorted(people), apps: sorted(apps) };
}

export function counts(rows) {
  const active = rows.filter((r) => ACTIVE.has(r.status));
  return { active: active.length, bugs: active.filter((r) => r.type === "bug").length, total: rows.length };
}

/** "Safari on iPhone", "Chrome on Mac"... from a user agent; "" when unknown. */
export function describeBrowser(ua) {
  if (typeof ua !== "string" || !ua) return "";
  const browser = /Edg\//.test(ua) ? "Edge"
    : /OPR\//.test(ua) ? "Opera"
    : /Firefox\/|FxiOS\//.test(ua) ? "Firefox"
    : /CriOS\/|Chrome\//.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari" : "";
  const device = /iPhone/.test(ua) ? "iPhone"
    : /iPad/.test(ua) ? "iPad"
    : /Android/.test(ua) ? "Android"
    : /Macintosh|Mac OS X/.test(ua) ? "Mac"
    : /Windows/.test(ua) ? "Windows"
    : /Linux/.test(ua) ? "Linux" : "";
  return [browser, device].filter(Boolean).join(" on ");
}

/** The open ids as "jobId abc · view rig". */
export function describeIds(ids) {
  if (!ids || typeof ids !== "object") return "";
  return Object.entries(ids).map(([k, v]) => `${k} ${v}`).join(" · ");
}
